import { apiClient } from '../api-client';
import { TICKETS } from '../../api-endpoints/api-endpoints';
import {
  ApiTicket, CreateTicketInput, ListTicketsResponse, ListTicketsQuery,
} from '../../schema/tickets/ticket.schema';

function buildQuery(params: object): string {
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return q ? `?${q}` : '';
}

export const ticketsService = {
  /**
   * List tickets.
   *
   * Node filtering (SKTICK-CR-001 WP-2) rides here as `nodeId` (exact) and
   * `nodeIdIn` (a comma-separated, ALREADY-RESOLVED set). SK Tickets does not
   * resolve the SK Platform hierarchy — the caller sends the subtree it wants
   * and the server filters on exactly that.
   *
   * Responses carry the resolved `sla` block per ticket. Render it; never
   * recompute overdue client-side (WP-5).
   */
  list: (query?: ListTicketsQuery): Promise<ListTicketsResponse> =>
    apiClient.get<ListTicketsResponse>(TICKETS.list + buildQuery(query ?? {})),

  get: (id: string): Promise<ApiTicket> =>
    apiClient.get<ApiTicket>(TICKETS.get(id)),

  getByNumber: (n: number): Promise<ApiTicket> =>
    apiClient.get<ApiTicket>(TICKETS.byNumber(n)),

  create: (body: CreateTicketInput): Promise<ApiTicket> =>
    apiClient.post<ApiTicket>(TICKETS.create, body),

  updateStatus: (id: string, status: string, resolutionSummary?: string): Promise<ApiTicket> =>
    apiClient.patch<ApiTicket>(TICKETS.status(id), { status, ...(resolutionSummary ? { resolutionSummary } : {}) }),

  assign: (id: string, platformUserId?: string, assignedGroup?: string): Promise<ApiTicket> =>
    apiClient.patch<ApiTicket>(TICKETS.assign(id), { platformUserId, assignedGroup }),

  remove: (id: string): Promise<void> =>
    apiClient.delete<void>(TICKETS.delete(id)),

  bulkStatus: (ticketIds: string[], status: string): Promise<{ updated: number; notFound: string[] }> =>
    apiClient.post(TICKETS.bulkStatus, { ticketIds, status }),

  bulkAssign: (ticketIds: string[], platformUserId?: string, assignedGroup?: string) =>
    apiClient.post(TICKETS.bulkAssign, { ticketIds, platformUserId, assignedGroup }),
};
